import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  LinearProgress,
  Fade,
  Slide,
  AppBar,
  Toolbar,
  Container,
  Stack,
  Tooltip,
  Avatar,
  FormHelperText,
  SwipeableDrawer,
  Tab,
  Tabs,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Fab,
  Badge,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  InputAdornment,
  CircularProgress,
  DialogActions,
  DialogContent as MuiDialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DeleteOutline,
  ExpandMore,
  Add,
  Save,
  Public,
  PublicOff,
  QrCode,
  DragIndicator,
  Image,
  Link,
  Visibility,
  Business,
  Person,
  Cancel,
  ContactPage,
  ViewModule,
  ViewQuilt,
  Info,
  Star,
  Animation,
  Link as LinkIcon,
  Settings,
  List,
  Preview,
  Palette,
  Edit,
  Close,
  HelpOutline,
  Upload,
  CloudUpload,
  PhotoCamera,
  Videocam,
  Phone,
  Email,
  CheckCircle,
  Warning,
  AccountCircle,
  Pages,
  Error,
  SwapHoriz,
  AccessTime,
  RefreshOutlined,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import API from "../../../../API";
import AlertDialog from "../../../../Components/AlertDialog";
import useAlertDialog from "../../../../Components/useAlertDialog";
import { EnhancedBioPagePreview } from "./previewhelper";
import UploadFileDialog from "../../../../Components/tools/uploadfile";
import HelpWizard from "./helpWizard";
import { styled } from "@mui/material/styles";
import ColorPickerGrid from "./colorpickergrid";
import UnifiedProfileSettings from "../../../../GlobalPages/globalUser/UnitedProfileSettins";
import {
  validateSlug,
  suggestAlternatives,
  isProhibitedSlug,
  getProhibitedSlugs,
} from "./prohibitedSlugs";

// ************************************************************
// CONSTANTS FOR APPSHELL BOTTOM NAV SPACING
// ************************************************************
const APPSHELL_BAR_HEIGHT = 86; // matches AppShell BAR_HEIGHT
const SPEED_DIAL_MARGIN = 16;   // additional margin for spacing

// ************************************************************
// MOBILE-SPECIFIC STYLED COMPONENTS
// ************************************************************
const MobileCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
  margin: theme.spacing(1),
  "&:active": {
    transform: "scale(0.98)",
    transition: "transform 0.1s",
  },
}));

const PageChipMobile = styled(Chip)(({ theme, selected }) => ({
  height: 56,
  borderRadius: theme.spacing(4),
  fontSize: "0.875rem",
  fontWeight: 600,
  minWidth: 120,
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : `2px solid ${theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.primary.main : "transparent",
  color: selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  "&:active": {
    transform: "scale(0.95)",
    transition: "transform 0.1s",
  },
}));

const MobileBottomSheet = styled(SwipeableDrawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    borderTopLeftRadius: theme.spacing(3),
    borderTopRightRadius: theme.spacing(3),
    maxHeight: `calc(95vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))`,
    overflowY: "auto",
    paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(2)})`,
    paddingTop: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(2)})`,
  },
}));

const MediaUploadZoneMobile = styled(Box)(({ theme, hasMedia }) => ({
  border: hasMedia
    ? `2px solid ${theme.palette.success.main}`
    : `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  textAlign: "center",
  backgroundColor: hasMedia
    ? theme.palette.success.light + "20"
    : theme.palette.grey[50],
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:active": {
    backgroundColor: theme.palette.primary.light + "10",
    borderColor: theme.palette.primary.main,
  },
}));

const HelpButton = styled(IconButton)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: theme.palette.info.light + "30",
  color: theme.palette.info.main,
  "&:hover": {
    backgroundColor: theme.palette.info.light + "50",
  },
}));

// Tab Panel Component
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pb: 2, pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Mobile tooltip wrapper that works better on touch
const MobileTooltip = ({ title, children, showHelper = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  if (showHelper) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        {children}
        <Tooltip title={title} placement="top">
          <HelpButton size="small">
            <Info fontSize="small" />
          </HelpButton>
        </Tooltip>
      </Box>
    );
  }
  return (
    <Tooltip title={title} placement="top" arrow>
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center" }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

// ************************************************************
// MAIN MOBILE COMPONENT
// ************************************************************
export default function CreatorBioMobile() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const theme = useTheme();

  // Main tab state (Profile, Bio Pages, Manage Transfers)
  const [mainTab, setMainTab] = useState(0);
  // Sub tab for Bio Pages (Items, Preview)
  const [currentTab, setCurrentTab] = useState(0);

  // External Transfers state
  const [transferStatusFilter, setTransferStatusFilter] = useState('pending');
  const [includeExpired, setIncludeExpired] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [editForm, setEditForm] = useState({ expires_in_days: '', recipient_email: '', recipient_phone: '' });

  // ---------------------
  // QUERIES & MUTATIONS
  // ---------------------
  const {
    data: pages,
    isLoading: isPagesLoading,
    error: pagesError,
  } = useQuery(["bioPages"], API.bio.list, {
    refetchOnWindowFocus: false,
  });

  const { data: availableContacts, isLoading: isContactsLoading } = useQuery(
    ["bioContacts"],
    API.bio.getAvailableContacts,
    {
      refetchOnWindowFocus: false,
    },
  );

  // External transfers query
  const {
    data: transfersData,
    isLoading: isTransfersLoading,
    refetch: refetchTransfers,
  } = useQuery(
    ["externalTransfers", transferStatusFilter, includeExpired],
    () => API.balanceLedger.listExternalTransfers({
      status: transferStatusFilter,
      include_expired: includeExpired,
      limit: 50,
      offset: 0,
    }),
    {
      refetchOnWindowFocus: false,
      enabled: mainTab === 2,
    }
  );

  // ---------------------
  // MOBILE-SPECIFIC STATE
  // ---------------------
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [isHelpWizardOpen, setIsHelpWizardOpen] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isProfileUploadOpen, setIsProfileUploadOpen] = useState(false);
  const [profilePhotoCallback, setProfilePhotoCallback] = useState(null);

  // Page state
  const [isNewPage, setIsNewPage] = useState(false);
  const [selectedPageIndex, setSelectedPageIndex] = useState(-1);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [pageType, setPageType] = useState("BUSINESS");
  const [viewType, setViewType] = useState("simple");
  const [linkedContactId, setLinkedContactId] = useState(null);
  const [linkUserPage, setLinkUserPage] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const [animationType, setAnimationType] = useState("FADE_IN");
  const [isPublic, setIsPublic] = useState(true);
  const [qrEnabled, setQrEnabled] = useState(false);
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [themeObj, setThemeObj] = useState({
    backgroundColor: "#ffffff",
    foregroundColor: "#000000",
  });
  const [items, setItems] = useState([]);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [isBackgroundPickerOpen, setIsBackgroundPickerOpen] = useState(false);
  const [isForegroundPickerOpen, setIsForegroundPickerOpen] = useState(false);

  // Slug validation state
  const [slugValidation, setSlugValidation] = useState({
    isValid: true,
    message: "",
  });
  const [slugSuggestions, setSlugSuggestions] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  // Real-time slug validation
  useEffect(() => {
    if (slug && isNewPage) {
      const validation = validateSlug(slug);
      setSlugValidation(validation);
      if (!validation.isValid && isProhibitedSlug(slug)) {
        const suggestions = suggestAlternatives(slug);
        setSlugSuggestions(suggestions);
        // Show snackbar for prohibited slugs
        setSnackbarMessage(
          `"${slug}" is reserved. Try one of the suggestions below!`,
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      } else {
        setSlugSuggestions([]);
      }
    } else if (!slug && isNewPage) {
      setSlugValidation({ isValid: false, message: "" });
      setSlugSuggestions([]);
    }
  }, [slug, isNewPage]);

  // ---------------------
  // MUTATIONS
  // ---------------------
  const cancelTransferMutation = useMutation(
    (transferId) => API.balanceLedger.cancelExternalTransfer(transferId),
    {
      onSuccess: () => {
        setCancelDialogOpen(false);
        setSelectedTransfer(null);
        queryClient.invalidateQueries(["externalTransfers"]);
        setSnackbarMessage("Transfer cancelled and funds refunded successfully");
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      },
      onError: (error) => {
        setSnackbarMessage(error?.response?.data?.error || "Failed to cancel transfer");
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      },
    }
  );

  const updateTransferMutation = useMutation(
    ({ id, data }) => API.balanceLedger.updateExternalTransfer(id, data),
    {
      onSuccess: (data) => {
        setEditDialogOpen(false);
        setSelectedTransfer(null);
        setEditForm({ expires_in_days: '', recipient_email: '', recipient_phone: '' });
        queryClient.invalidateQueries(["externalTransfers"]);
        const messages = [];
        if (data?.resend_claim_to_email) messages.push(`Claim email sent to ${data.resend_claim_to_email}`);
        if (data?.resend_claim_to_phone) messages.push(`Claim SMS sent to ${data.resend_claim_to_phone}`);
        setSnackbarMessage(messages.length > 0 ? `Transfer updated. ${messages.join('. ')}` : "Transfer updated successfully");
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      },
      onError: (error) => {
        setSnackbarMessage(error?.response?.data?.error || "Failed to update transfer");
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      },
    }
  );

  const createMutation = useMutation(
    (newPageData) => API.bio.create(newPageData),
    {
      onSuccess: () => {
        openDialog("Success", "Bio page created successfully! 🎉", () => {
          closeDialog();
          queryClient.invalidateQueries(["bioPages"]);
          setIsEditDrawerOpen(false);
          // Reset to initial state
          setSelectedPageIndex(-1);
          setIsNewPage(false);
        });
      },
      onError: (error) => {
        openDialog(
          "Error",
          error?.response?.data?.error || error.message,
          closeDialog,
          closeDialog,
        );
      },
    },
  );

  const updateMutation = useMutation(
    ({ oldSlug, updated }) => API.bio.update(oldSlug, updated),
    {
      onSuccess: () => {
        openDialog("Success", "Bio page updated successfully! ✨", () => {
          closeDialog();
          queryClient.invalidateQueries(["bioPages"]);
          setIsEditDrawerOpen(false);
        });
      },
      onError: (error) => {
        openDialog(
          "Error",
          error?.response?.data?.error || error.message,
          closeDialog,
          closeDialog,
        );
      },
    },
  );

  // ---------------------
  // LOADING & ERROR STATES
  // ---------------------
  if (isPagesLoading) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: "center",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          pt: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(3)})`,
          pb: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(3)})`,
        }}
      >
        <Business sx={{ fontSize: 64, color: "primary.main", mb: 3 }} />
        <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
          Loading Bio Pages
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Getting your pages ready...
        </Typography>
        <LinearProgress
          sx={{
            mx: "auto",
            width: "80%",
            height: 8,
            borderRadius: 4,
          }}
        />
      </Box>
    );
  }

  if (pagesError) {
    return (
      <Box
        sx={{
          p: 3,
          pt: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(3)})`,
          pb: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(3)})`,
        }}
      >
        <Alert severity="error" sx={{ borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Error Loading Bio Pages
          </Typography>
          <Typography>
            {pagesError?.response?.data?.error || pagesError.message}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<HelpOutline />}
          onClick={() => setIsHelpWizardOpen(true)}
          fullWidth
        >
          Get Help
        </Button>
      </Box>
    );
  }

  // ---------------------
  // EVENT HANDLERS
  // ---------------------
  function handleSelectPage(index) {
    if (index === -1) {
      // Creating a new page
      setIsNewPage(true);
      setSelectedPageIndex(-1);
      setSlug("");
      setTitle("");
      setDescription("");
      setPageType("BUSINESS");
      setViewType("simple");
      setLinkedContactId(null);
      setLinkUserPage(false);
      setAnimationsEnabled(true);
      setAnimationType("FADE_IN");
      setIsPublic(true);
      setQrEnabled(false);
      setHeaderMediaUrl("");
      setThemeObj({ backgroundColor: "#ffffff", foregroundColor: "#000000" });
      setItems([]);
      setSlugValidation({ isValid: false, message: "" });
      setSlugSuggestions([]);
      setShowValidationErrors(false);
    } else {
      // Editing existing page
      setIsNewPage(false);
      setSelectedPageIndex(index);
      const page = pages[index];
      setSlug(page.slug);
      setTitle(page.title || "");
      setDescription(page.description || "");
      setPageType(page.page_type || "BUSINESS");
      setViewType(page.view_type || "simple");
      setLinkedContactId(page.linked_contact_id);
      setLinkUserPage(page.link_user_page || false);
      setAnimationsEnabled(page.animations_enabled !== false);
      setAnimationType(page.animation_type || "FADE_IN");
      setIsPublic(page.is_public);
      setQrEnabled(page.qr_enabled);
      setHeaderMediaUrl(page.header_media_url || "");
      setThemeObj(
        page.theme || {
          backgroundColor: "#ffffff",
          foregroundColor: "#000000",
        },
      );
      setItems(page.items || []);
      setSlugValidation({ isValid: true, message: "" });
      setSlugSuggestions([]);
      setShowValidationErrors(false);
    }
  }

  function handleAddItem() {
    const newItem = {
      component_type: "LINK",
      label: "New Link",
      url: "https://example.com",
      media_url: "",
      size: "MEDIUM",
      is_enabled: true,
    };
    setItems((prev) => [...prev, newItem]);
  }

  function handleRemoveItem(idx) {
    const updated = [...items];
    updated.splice(idx, 1);
    setItems(updated);
  }

  function handleUpdateItemField(idx, field, value) {
    setItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  }

  function handleSave() {
    if (isNewPage) {
      const validation = validateSlug(slug);
      if (!validation.isValid) {
        setShowValidationErrors(true);
        openDialog(
          "Validation Error",
          validation.message,
          closeDialog,
          closeDialog,
        );
        return;
      }
    }
    if (!slug) {
      setShowValidationErrors(true);
      openDialog(
        "Validation Error",
        "A slug is required to create your bio page. This becomes your unique URL /@yourslug",
        closeDialog,
        closeDialog,
      );
      return;
    }

    const payload = {
      slug,
      title,
      description,
      page_type: pageType,
      view_type: viewType,
      linked_contact_id: linkedContactId,
      link_user_page: pageType === "PROFILE" ? linkUserPage : false,
      animations_enabled: animationsEnabled,
      animation_type: animationType,
      is_public: isPublic,
      qr_enabled: qrEnabled,
      theme: themeObj,
      header_media_url: headerMediaUrl,
      items: items.map((item, idx) => ({ ...item, order_index: idx })),
    };

    if (isNewPage) {
      createMutation.mutate(payload);
    } else {
      const oldSlug = pages[selectedPageIndex].slug;
      updateMutation.mutate({ oldSlug, updated: payload });
    }
  }

  function onDragEnd(result) {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;
    setItems((prev) => {
      const updatedItems = [...prev];
      const [moved] = updatedItems.splice(source.index, 1);
      updatedItems.splice(destination.index, 0, moved);
      return updatedItems;
    });
  }

  const selectedPage = selectedPageIndex >= 0 ? pages[selectedPageIndex] : null;
  const linkedContact = selectedPage?.linked_contact;

  // ---------------------
  // MOBILE RENDER HELPERS
  // ---------------------
  const renderMobilePageSelector = () => (
    <Box
      sx={{
        p: 2,
        pb: 2,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6" fontWeight="bold">
          Your Bio Pages
        </Typography>
        <MobileTooltip title="Learn about creating bio pages">
          <Button
            size="small"
            variant="text"
            startIcon={<HelpOutline />}
            onClick={() => setIsHelpWizardOpen(true)}
          >
            Help
          </Button>
        </MobileTooltip>
      </Box>
      <Stack direction="row" spacing={2} sx={{ overflowX: "auto", pb: 2 }}>
        {pages?.map((page, i) => (
          <MobileTooltip
            key={page.id}
            title={`${page.page_type === "PROFILE" ? "Profile" : "Business"} page: /@${page.slug}`}
          >
            <PageChipMobile
              selected={selectedPageIndex === i && !isNewPage}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {page.page_type === "PROFILE" && <Star fontSize="small" />}
                  <Typography variant="body2" fontWeight="inherit">
                    {page.slug}
                  </Typography>
                  <Badge
                    badgeContent={page.items?.length || 0}
                    color="secondary"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.6rem",
                        minWidth: 14,
                        height: 14,
                      },
                    }}
                  >
                    <Box />
                  </Badge>
                </Box>
              }
              onClick={() => {
                handleSelectPage(i);
                setIsEditDrawerOpen(true);
              }}
              clickable
            />
          </MobileTooltip>
        ))}
        <MobileTooltip title="Create a new bio page">
          <PageChipMobile
            selected={isNewPage}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Add fontSize="small" />
                <Typography variant="body2" fontWeight="inherit">
                  New
                </Typography>
              </Box>
            }
            onClick={() => {
              handleSelectPage(-1);
              setIsEditDrawerOpen(true);
            }}
            clickable
          />
        </MobileTooltip>
      </Stack>

      {/* Info card when no page is selected */}
      {selectedPageIndex === -1 && !isNewPage && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "grey.50",
            textAlign: "center",
          }}
        >
          <Pages sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Select a Page to Edit
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Choose an existing page or create a new one
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              handleSelectPage(-1);
              setIsEditDrawerOpen(true);
            }}
          >
            Create New Page
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderMobileItemsList = () => {
    // Show message when no page is selected
    if (selectedPageIndex === -1 && !isNewPage) {
      return (
        <Box
          textAlign="center"
          py={6}
          sx={{
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "grey.50",
            m: 2,
          }}
        >
          <Pages sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Page Selected
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Select a page to view and edit its items
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {items.length === 0 ? (
          <Box
            textAlign="center"
            py={6}
            sx={{
              border: "2px dashed",
              borderColor: "divider",
              borderRadius: 3,
              bgcolor: "grey.50",
              m: 2,
            }}
          >
            <Link sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No items yet
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              Items are the links and content on your bio page
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddItem}
            >
              Add First Item
            </Button>
          </Box>
        ) : (
          <Stack spacing={2} sx={{ p: 2 }}>
            {items.map((item, idx) => (
              <MobileCard key={idx}>
                <Accordion sx={{ boxShadow: "none" }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" gap={2} flex={1}>
                      <MobileTooltip title="Drag to reorder (desktop only)">
                        <DragIndicator color="action" />
                      </MobileTooltip>
                      <Link color="primary" />
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {item.label || "(Untitled)"}
                        </Typography>
                        {item.url && (
                          <Typography variant="caption" color="text.secondary">
                            {new URL(item.url).hostname}
                          </Typography>
                        )}
                      </Box>
                      <Chip label={item.size} size="small" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={3}>
                      <MobileTooltip
                        title="The text visitors will see for this link"
                        showHelper
                      >
                        <TextField
                          label="Label"
                          size="small"
                          value={item.label}
                          onChange={(e) =>
                            handleUpdateItemField(idx, "label", e.target.value)
                          }
                          fullWidth
                          placeholder="e.g., My Website, Instagram"
                        />
                      </MobileTooltip>
                      <MobileTooltip
                        title="Where this link goes when clicked"
                        showHelper
                      >
                        <TextField
                          label="URL"
                          size="small"
                          value={item.url || ""}
                          onChange={(e) =>
                            handleUpdateItemField(idx, "url", e.target.value)
                          }
                          fullWidth
                          placeholder="https://example.com"
                        />
                      </MobileTooltip>
                      <MobileTooltip
                        title="Optional description for more context"
                        showHelper
                      >
                        <TextField
                          label="Description"
                          size="small"
                          multiline
                          rows={2}
                          value={item.description || ""}
                          onChange={(e) =>
                            handleUpdateItemField(
                              idx,
                              "description",
                              e.target.value,
                            )
                          }
                          fullWidth
                          placeholder="Optional description..."
                        />
                      </MobileTooltip>
                      <Box display="flex" gap={2} alignItems="center">
                        <MobileTooltip title="Size affects prominence on your page">
                          <FormControl
                            size="small"
                            sx={{ minWidth: 100, flex: 1 }}
                          >
                            <InputLabel>Size</InputLabel>
                            <Select
                              value={item.size || "MEDIUM"}
                              label="Size"
                              onChange={(e) =>
                                handleUpdateItemField(idx, "size", e.target.value)
                              }
                            >
                              <MenuItem value="SMALL">Small</MenuItem>
                              <MenuItem value="MEDIUM">Medium</MenuItem>
                              <MenuItem value="LARGE">Large</MenuItem>
                            </Select>
                          </FormControl>
                        </MobileTooltip>
                        <MobileTooltip title="Add image or video to make this item stand out">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Upload />}
                            onClick={() => {
                              setUploadTarget(idx);
                              setIsUploadDialogOpen(true);
                            }}
                          >
                            {item.media_url ? "Change" : "Media"}
                          </Button>
                        </MobileTooltip>
                        <MobileTooltip title="Remove this item">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleRemoveItem(idx)}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </MobileTooltip>
                      </Box>
                      {/* Media preview */}
                      {item.media_url && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            gutterBottom
                            display="block"
                          >
                            Current Media:
                          </Typography>
                          {/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(
                            item.media_url,
                          ) ? (
                            <Box
                              component="img"
                              src={item.media_url}
                              alt="Item media"
                              sx={{
                                width: "100%",
                                maxHeight: 120,
                                borderRadius: 2,
                                objectFit: "cover",
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                height: 120,
                                bgcolor: "grey.100",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Videocam color="action" />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                Video
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </MobileCard>
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  const renderMobilePreview = () => {
    // Show message when no page is selected
    if (selectedPageIndex === -1 && !isNewPage) {
      return (
        <Box
          textAlign="center"
          py={6}
          sx={{
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: "grey.50",
            m: 2,
          }}
        >
          <Visibility sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Preview Available
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Select a page to see how it looks
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: 1,
          pb: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(1)})`,
        }}
      >
        <Box
          sx={{
            border: "2px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <EnhancedBioPagePreview
            title={title}
            slug={slug}
            description={description}
            pageType={pageType}
            viewType={viewType}
            items={items}
            isPublic={isPublic}
            qrEnabled={qrEnabled}
            animationsEnabled={animationsEnabled}
            animationType={animationType}
            linkUserPage={linkUserPage}
            headerMediaUrl={headerMediaUrl}
            linkedContact={linkedContact}
            customtheme={themeObj}
          />
        </Box>
      </Box>
    );
  };

  // ---------------------
  // MOBILE SETTINGS DRAWER
  // ---------------------
  const renderMobileSettings = () => (
    <MobileBottomSheet
      anchor="bottom"
      open={isSettingsOpen}
      onClose={() => setIsSettingsOpen(false)}
      onOpen={() => setIsSettingsOpen(true)}
    >
      <Box
        sx={{
          p: 3,
          pb: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(6)})`,
          pt: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(3)})`,
          overflowY: "auto",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" fontWeight="bold">
            Page Settings
          </Typography>
          <IconButton onClick={() => setIsSettingsOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <Stack spacing={4}>
          {/* Page Type */}
          <Box>
            <MobileTooltip
              title="Profile pages are special - you can only have one. Business pages are unlimited."
              showHelper
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                color="primary"
                fontWeight="bold"
              >
                Page Type
              </Typography>
            </MobileTooltip>
            <Stack direction="row" spacing={2}>
              <Button
                variant={pageType === "PROFILE" ? "contained" : "outlined"}
                color="success"
                startIcon={<Star />}
                onClick={() => setPageType("PROFILE")}
                fullWidth
              >
                Profile
              </Button>
              <Button
                variant={pageType === "BUSINESS" ? "contained" : "outlined"}
                color="primary"
                startIcon={<Business />}
                onClick={() => setPageType("BUSINESS")}
                fullWidth
              >
                Business
              </Button>
            </Stack>
            {pageType === "PROFILE" && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                <Typography variant="body2">
                  Profile pages are your main bio page - you can only have one.
                </Typography>
              </Alert>
            )}
          </Box>
          {/* View Type */}
          <Box>
            <MobileTooltip
              title="Choose how your items are displayed - simple or complex layout"
              showHelper
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                color="primary"
                fontWeight="bold"
              >
                Layout Style
              </Typography>
            </MobileTooltip>
            <FormControl fullWidth>
              <Select
                value={viewType}
                onChange={(e) => setViewType(e.target.value)}
              >
                <MenuItem value="simple">
                  <Box display="flex" alignItems="center" gap={2}>
                    <ViewModule />
                    <Box>
                      <Typography>Simple Layout</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Clean, minimal design
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="complex">
                  <Box display="flex" alignItems="center" gap={2}>
                    <ViewQuilt />
                    <Box>
                      <Typography>Complex Layout</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Rich design with grid
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          {/* Header Media */}
          <Box>
            <MobileTooltip
              title="Add a banner image or video to make your page stand out"
              showHelper
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                color="primary"
                fontWeight="bold"
              >
                Header Media
              </Typography>
            </MobileTooltip>
            <MediaUploadZoneMobile
              hasMedia={!!headerMediaUrl}
              onClick={() => {
                setUploadTarget("header");
                setIsUploadDialogOpen(true);
              }}
            >
              {headerMediaUrl ? (
                <Box>
                  <CheckCircle color="success" sx={{ mb: 1 }} />
                  <Typography variant="body2" fontWeight={600}>
                    Media Added
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tap to change
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUpload
                    sx={{ fontSize: 32, color: "text.disabled", mb: 1 }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    Upload Header Media
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Banner image or video
                  </Typography>
                </Box>
              )}
            </MediaUploadZoneMobile>
          </Box>
          {/* Animation Settings */}
          <Box>
            <MobileTooltip
              title="Add smooth animations to make your page more engaging"
              showHelper
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                color="primary"
                fontWeight="bold"
              >
                Animations
              </Typography>
            </MobileTooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={animationsEnabled}
                  onChange={(e) => setAnimationsEnabled(e.target.checked)}
                />
              }
              label="Enable Animations"
            />
            {animationsEnabled && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Animation Type</InputLabel>
                <Select
                  value={animationType}
                  label="Animation Type"
                  onChange={(e) => setAnimationType(e.target.value)}
                >
                  <MenuItem value="FADE_IN">Fade In</MenuItem>
                  <MenuItem value="SLIDE_UP">Slide Up</MenuItem>
                </Select>
                <FormHelperText>
                  How items appear when the page loads
                </FormHelperText>
              </FormControl>
            )}
          </Box>
          {/* Profile Page Linking */}
          {pageType === "PROFILE" && (
            <Box>
              <MobileTooltip
                title="Link your profile page to your main user/mediakit page"
                showHelper
              >
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  color="primary"
                  fontWeight="bold"
                >
                  Page Linking
                </Typography>
              </MobileTooltip>
              <FormControlLabel
                control={
                  <Switch
                    checked={linkUserPage}
                    onChange={(e) => setLinkUserPage(e.target.checked)}
                  />
                }
                label="Link to User/Mediakit Page"
              />
              <FormHelperText>
                Adds a button linking to your main profile page
              </FormHelperText>
            </Box>
          )}
          {/* Contact Integration */}
          <Box>
            <MobileTooltip
              title="Link a contact from your CRM to display their information"
              showHelper
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                color="primary"
                fontWeight="bold"
              >
                Contact Integration
              </Typography>
            </MobileTooltip>
            <FormControl fullWidth>
              <InputLabel>Link Contact (Optional)</InputLabel>
              <Select
                value={linkedContactId || ""}
                label="Link Contact (Optional)"
                onChange={(e) => setLinkedContactId(e.target.value || null)}
              >
                <MenuItem value="">
                  <em>No contact linked</em>
                </MenuItem>
                {availableContacts?.map((contact) => (
                  <MenuItem key={contact.id} value={contact.id}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        {contact.first_name?.[0] ||
                          contact.full_name?.[0] ||
                          "?"}
                      </Avatar>
                      <Typography variant="body2">
                        {contact.full_name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Contact info will appear on your bio page
              </FormHelperText>
            </FormControl>
          </Box>
          {/* Privacy & Features */}
          <Box>
            <MobileTooltip
              title="Control who can see your page and enable special features"
              showHelper
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                color="primary"
                fontWeight="bold"
              >
                Privacy & Features
              </Typography>
            </MobileTooltip>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Public fontSize="small" />
                    <Typography variant="body2">Public Page</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={qrEnabled}
                    onChange={(e) => setQrEnabled(e.target.checked)}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <QrCode fontSize="small" />
                    <Typography variant="body2">QR Code</Typography>
                  </Box>
                }
              />
            </Stack>
          </Box>
          {/* Theme Controls */}
          <Box>
            <MobileTooltip
              title="Customize the colors of your bio page"
              showHelper
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                color="primary"
                fontWeight="bold"
              >
                Theme Colors
              </Typography>
            </MobileTooltip>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setIsBackgroundPickerOpen(true)}
                sx={{
                  bgcolor: themeObj.backgroundColor,
                  color: themeObj.foregroundColor,
                  flex: 1,
                }}
              >
                Background
              </Button>
              <Button
                variant="outlined"
                onClick={() => setIsForegroundPickerOpen(true)}
                sx={{
                  bgcolor: themeObj.foregroundColor,
                  color: themeObj.backgroundColor,
                  flex: 1,
                }}
              >
                Text
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </MobileBottomSheet>
  );

  // ---------------------
  // MOBILE EDIT DRAWER
  // ---------------------
  const renderMobileEditDrawer = () => (
    <MobileBottomSheet
      anchor="bottom"
      open={isEditDrawerOpen}
      onClose={() => setIsEditDrawerOpen(false)}
      onOpen={() => setIsEditDrawerOpen(true)}
    >
      <Box
        sx={{
          minHeight: "80vh",
          p: 3,
          pb: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(3)})`,
          pt: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(3)})`,
          overflowY: "auto",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6" fontWeight="bold">
            {isNewPage ? "Create Page" : "Edit Page"}
          </Typography>
          <IconButton onClick={() => setIsEditDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <Stack spacing={3}>
          {showValidationErrors && !slug && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Required:</strong> Please enter a slug to create your
                bio page URL
              </Typography>
            </Alert>
          )}
          {isNewPage && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Basic Information
              </Typography>
              <Tooltip title="Some slugs are reserved for system routes and cannot be used">
                <IconButton
                  size="small"
                  onClick={() => {
                    const count = getProhibitedSlugs().length;
                    setSnackbarMessage(
                      `${count} routes are reserved and cannot be used as slugs. We'll let you know if you try one!`,
                    );
                    setSnackbarSeverity("info");
                    setSnackbarOpen(true);
                  }}
                >
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <MobileTooltip
            title="Your page URL will be /your-slug - choose carefully, it cannot be changed!"
            showHelper
          >
            <TextField
              label="Slug (URL Path)"
              value={slug}
              onChange={(e) =>
                setSlug(
                  e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""),
                )
              }
              fullWidth
              disabled={!isNewPage}
              variant="outlined"
              error={(showValidationErrors || slug) && !slugValidation.isValid}
              helperText={
                slug
                  ? slugValidation.isValid
                    ? `Available at: /${slug}`
                    : slugValidation.message
                  : "Required - your page URL /your-slug"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography color="text.secondary">/</Typography>
                  </InputAdornment>
                ),
                endAdornment: slug && (
                  <InputAdornment position="end">
                    {slugValidation.isValid ? (
                      <CheckCircle color="success" />
                    ) : (
                      <Error color="error" />
                    )}
                  </InputAdornment>
                ),
              }}
              placeholder="your-name"
            />
          </MobileTooltip>
          {slugSuggestions.length > 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                💡 Try these alternatives:
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                mt={1}
              >
                {slugSuggestions.map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    size="small"
                    onClick={() => {
                      setSlug(suggestion);
                      setSnackbarMessage(`Updated slug to "${suggestion}"`);
                      setSnackbarSeverity("success");
                      setSnackbarOpen(true);
                    }}
                    clickable
                    color="primary"
                    variant="outlined"
                    icon={<CheckCircle />}
                  />
                ))}
              </Stack>
            </Alert>
          )}
          <MobileTooltip
            title="The main headline visitors will see - your name or brand"
            showHelper
          >
            <TextField
              label="Page Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              placeholder="John Smith, Creative Director"
            />
          </MobileTooltip>
          <MobileTooltip
            title="A brief description of who you are or what you do"
            showHelper
          >
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Tell visitors about yourself..."
            />
          </MobileTooltip>
          <Stack direction="row" spacing={2}>
            <MobileTooltip
              title={
                isNewPage
                  ? "Create your new bio page"
                  : "Save changes to your bio page"
              }
            >
              <Button
                variant="contained"
                color="success"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={
                  createMutation.isLoading ||
                  updateMutation.isLoading ||
                  (isNewPage && !slugValidation.isValid)
                }
                fullWidth
              >
                {isNewPage ? "Create" : "Update"}
              </Button>
            </MobileTooltip>
            <MobileTooltip title="Open advanced settings">
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => {
                  setIsEditDrawerOpen(false);
                  setIsSettingsOpen(true);
                }}
                fullWidth
              >
                Settings
              </Button>
            </MobileTooltip>
          </Stack>
        </Stack>
      </Box>
    </MobileBottomSheet>
  );

  // ---------------------
  // MAIN MOBILE RENDER
  // ---------------------
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.50",
        pb: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(10)})`,
        overflowY: "auto",
      }}
    >
      <AlertDialog alertState={dialogState} />
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%", borderRadius: 2 }}
          variant="filled"
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSnackbarOpen(false)}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* Main Navigation Tabs (Fixed at Top) */}
      <Paper
        sx={{
          position: "fixed",
          top: `env(safe-area-inset-top, 0px)`,
          left: 0,
          right: 0,
          zIndex: 1100,
          borderRadius: 0,
        }}
      >
        <Tabs
          value={mainTab}
          onChange={(e, newValue) => setMainTab(newValue)}
          variant="fullWidth"
          centered
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<AccountCircle />} label="Profile" />
          <Tab icon={<Pages />} label="Bio Pages" />
          <Tab icon={<SwapHoriz />} label="Transfers" />
        </Tabs>
      </Paper>
      {/* Content Area */}
      <Box sx={{ pt: `calc(env(safe-area-inset-top, 0px) + 56px)` }}>
        {mainTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
            >
              <Typography variant="h5" fontWeight="bold">
                Profile Settings 
              </Typography>
              <MobileTooltip title="Get help with profile settings">
                <IconButton
                  onClick={() => setIsHelpWizardOpen(true)}
                  sx={{ bgcolor: "primary.light", color: "primary.main" }}
                >
                  <HelpOutline />
                </IconButton>
              </MobileTooltip> 
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Update your personal or business profile information. This
              information is used across your bio pages and professional
              presence.
            </Typography>
            <UnifiedProfileSettings
              onUploadPhoto={(callback) => {
                setProfilePhotoCallback(() => callback);
                setIsProfileUploadOpen(true);
              }}
              compact={false}
              showContext={true}
            />
            <Box mt={4}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Pro Tip:</strong> Your profile information can be
                  automatically linked to your bio pages. Switch to the "Bio
                  Pages" tab to create or edit your pages.
                </Typography>
              </Alert>
            </Box>
          </Box>
        )}
        {mainTab === 2 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" mb={1.5}>
              Manage Transfers
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="flex-end" mb={2} gap={1} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={transferStatusFilter}
                  label="Status"
                  onChange={(e) => setTransferStatusFilter(e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="claimed">Claimed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
              {transferStatusFilter === 'pending' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeExpired}
                      onChange={(e) => setIncludeExpired(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="caption">Expired</Typography>}
                />
              )}
              <IconButton onClick={() => refetchTransfers()} size="small">
                <RefreshOutlined />
              </IconButton>
            </Box>

            {isTransfersLoading ? (
              <Box textAlign="center" py={6}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading transfers...
                </Typography>
              </Box>
            ) : transfersData?.transfers && transfersData.transfers.length > 0 ? (
              <>
                <Typography variant="caption" color="text.secondary" mb={1} display="block">
                  Showing {transfersData.transfers.length} of {transfersData.total_count || transfersData.transfers.length} transfers
                </Typography>
                <Stack spacing={1.5}>
                  {transfersData.transfers.map((transfer) => {
                    const isExpired = transfer.expires_at && new Date(transfer.expires_at) < new Date();
                    const statusColor = transfer.status === 'pending'
                      ? (isExpired ? 'warning' : 'info')
                      : transfer.status === 'claimed'
                        ? 'success'
                        : transfer.status === 'cancelled'
                          ? 'default'
                          : 'error';

                    return (
                      <Box
                        key={transfer.id}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                                ${parseFloat(transfer.amount || 0).toFixed(2)}
                              </Typography>
                              {transfer.currency && (
                                <Typography variant="caption" color="text.disabled">
                                  {transfer.currency}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label={isExpired && transfer.status === 'pending' ? 'Expired' : transfer.status}
                              color={statusColor}
                              size="small"
                              sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                            />
                          </Box>
                        </Box>

                        <Divider />

                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            {transfer.recipient_email && (
                              <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                                <Email sx={{ fontSize: 16 }} color="action" />
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                                  {transfer.recipient_email}
                                </Typography>
                              </Box>
                            )}
                            {transfer.recipient_phone && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Phone sx={{ fontSize: 16 }} color="action" />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {transfer.recipient_phone}
                                </Typography>
                              </Box>
                            )}
                            {transfer.recipient_name && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Person sx={{ fontSize: 16 }} color="action" />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {transfer.recipient_name}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                              Created: {new Date(transfer.created_at).toLocaleDateString()}
                            </Typography>
                            {transfer.expires_at && (
                              <Typography variant="caption" color={isExpired ? 'error.main' : 'text.disabled'} sx={{ fontSize: '0.65rem' }}>
                                Expires: {new Date(transfer.expires_at).toLocaleDateString()}
                              </Typography>
                            )}
                            {transfer.note && (
                              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', fontSize: '0.65rem' }} noWrap>
                                {transfer.note}
                              </Typography>
                            )}
                            {transfer.claim_token && (
                              <Chip label="Claim Link" size="small" variant="outlined" color="info" sx={{ height: 20, fontSize: '0.65rem' }} />
                            )}
                          </Stack>
                        </Box>

                        {transfer.status === 'pending' && !isExpired && (
                          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', flex: 1 }}
                              onClick={() => {
                                setSelectedTransfer(transfer);
                                setEditForm({
                                  expires_in_days: '',
                                  recipient_email: transfer.recipient_email || '',
                                  recipient_phone: transfer.recipient_phone || '',
                                });
                                setEditDialogOpen(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ fontSize: '0.7rem', flex: 1 }}
                              onClick={() => {
                                setSelectedTransfer(transfer);
                                setCancelDialogOpen(true);
                              }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </>
            ) : (
              <Box
                textAlign="center"
                py={6}
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 3,
                  bgcolor: 'grey.50',
                }}
              >
                <SwapHoriz sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No {transferStatusFilter === 'pending' ? 'Pending ' : ''}Transfers
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {transferStatusFilter === 'pending'
                    ? 'You have no pending external transfers at this time.'
                    : `No transfers with status "${transferStatusFilter}" found.`}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {mainTab === 1 && (
          <>
            {/* Page Selector */}
            {renderMobilePageSelector()}
            {/* Sub Tabs for Bio Pages */}
            <Paper
              sx={{
                position: "sticky",
                top: `calc(env(safe-area-inset-top, 0px) + 56px)`,
                zIndex: 100,
                borderRadius: 0,
              }}
            >
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                variant="fullWidth"
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab icon={<List />} label="Items" />
                <Tab icon={<Preview />} label="Preview" />
              </Tabs>
            </Paper>
            {/* Sub Tab Content */}
            <Box sx={{ pt: 7 }}>
              <TabPanel value={currentTab} index={0}>
                {renderMobileItemsList()}
              </TabPanel>
              <TabPanel value={currentTab} index={1}>
                {renderMobilePreview()}
              </TabPanel>
            </Box>
          </>
        )}
      </Box>
      {/* Speed Dial (only for Bio Pages with a selected page) - UPDATED POSITION */}
      {mainTab === 1 && (selectedPageIndex !== -1 || isNewPage) && (
        <SpeedDial
          ariaLabel="Bio Page Actions"
          sx={{
            position: "fixed",
            bottom: `calc(${APPSHELL_BAR_HEIGHT}px + ${SPEED_DIAL_MARGIN}px + env(safe-area-inset-bottom, 0px))`,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
          icon={<SpeedDialIcon />}
          open={speedDialOpen}
          onClose={() => setSpeedDialOpen(false)}
          onOpen={() => setSpeedDialOpen(true)}
        >
          <SpeedDialAction
            key="add-item"
            icon={<Add />}
            tooltipTitle="Add Item"
            onClick={() => {
              handleAddItem();
              setSpeedDialOpen(false);
            }}
          />
          <SpeedDialAction
            key="settings"
            icon={<Settings />}
            tooltipTitle="Settings"
            onClick={() => {
              setIsSettingsOpen(true);
              setSpeedDialOpen(false);
            }}
          />
          <SpeedDialAction
            key="preview"
            icon={<Visibility />}
            tooltipTitle="Full Preview"
            onClick={() => {
              setIsPreviewOpen(true);
              setSpeedDialOpen(false);
            }}
          />
          <SpeedDialAction
            key="help"
            icon={<HelpOutline />}
            tooltipTitle="Get Help"
            onClick={() => {
              setIsHelpWizardOpen(true);
              setSpeedDialOpen(false);
            }}
          />
        </SpeedDial>
      )}
      {/* Mobile Drawers */}
      {renderMobileEditDrawer()}
      {renderMobileSettings()}
      {/* Full Preview Dialog */}
      <Dialog
        fullScreen
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        sx={{
          "& .MuiDialog-paper": {
            pt: `calc(env(safe-area-inset-top, 0px))`,
            pb: `calc(env(safe-area-inset-bottom, 0px))`,
          },
        }}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setIsPreviewOpen(false)}
            >
              <Cancel />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
              Preview - {title || slug || "Untitled"}
            </Typography>
            <Typography variant="body2">/@{slug}</Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ overflowY: "auto", height: "100%" }}>
          <EnhancedBioPagePreview
            title={title}
            slug={slug}
            description={description}
            pageType={pageType}
            viewType={viewType}
            items={items}
            isPublic={isPublic}
            qrEnabled={qrEnabled}
            animationsEnabled={animationsEnabled}
            animationType={animationType}
            linkUserPage={linkUserPage}
            headerMediaUrl={headerMediaUrl}
            linkedContact={linkedContact}
            customtheme={themeObj}
          />
        </Box>
      </Dialog>
      {/* Help Wizard */}
      <HelpWizard
        open={isHelpWizardOpen}
        onClose={() => setIsHelpWizardOpen(false)}
      />
      {/* Upload Dialog (for bio items/header) */}
      <UploadFileDialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onUploadSuccess={(data) => {
          const uploadedUrl = data.url;
          if (uploadedUrl) {
            if (uploadTarget === "header") {
              setHeaderMediaUrl(uploadedUrl);
            } else if (typeof uploadTarget === "number") {
              handleUpdateItemField(uploadTarget, "media_url", uploadedUrl);
            }
          }
        }}
      />
      {/* Profile Upload Dialog */}
      <UploadFileDialog
        open={isProfileUploadOpen}
        onClose={() => setIsProfileUploadOpen(false)}
        onUploadSuccess={(data) => {
          const uploadedUrl = data.url;
          if (uploadedUrl && profilePhotoCallback) {
            profilePhotoCallback(uploadedUrl);
            queryClient.invalidateQueries(["user"]);
            queryClient.invalidateQueries(["creator"]);
          }
        }}
      />
      {/* Color Picker Dialogs */}
      <ColorPickerGrid
        open={isBackgroundPickerOpen}
        onClose={() => setIsBackgroundPickerOpen(false)}
        selectedColor={themeObj.backgroundColor || "#FFFFFF"}
        onColorSelect={(color) =>
          setThemeObj((prev) => ({ ...prev, backgroundColor: color }))
        }
      />
      <ColorPickerGrid
        open={isForegroundPickerOpen}
        onClose={() => setIsForegroundPickerOpen(false)}
        selectedColor={themeObj.foregroundColor || "#000000"}
        onColorSelect={(color) =>
          setThemeObj((prev) => ({ ...prev, foregroundColor: color }))
        }
      />
      {/* Cancel Transfer Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => { setCancelDialogOpen(false); setSelectedTransfer(null); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancel Transfer</DialogTitle>
        <MuiDialogContent>
          <DialogContentText>
            Are you sure you want to cancel this transfer of <strong>${parseFloat(selectedTransfer?.amount || 0).toFixed(2)}</strong>?
            The funds will be refunded to your balance.
          </DialogContentText>
          {selectedTransfer?.recipient_email && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Recipient: {selectedTransfer.recipient_email}
            </Typography>
          )}
        </MuiDialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelDialogOpen(false); setSelectedTransfer(null); }}>
            Keep Transfer
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelTransferMutation.isLoading}
            onClick={() => {
              if (selectedTransfer) {
                cancelTransferMutation.mutate(selectedTransfer.id);
              }
            }}
          >
            {cancelTransferMutation.isLoading ? 'Cancelling...' : 'Cancel & Refund'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Transfer Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setSelectedTransfer(null); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Transfer</DialogTitle>
        <MuiDialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Update the transfer details below. Changing the recipient email or phone will resend the claim link to the new contact.
          </DialogContentText>
          <Stack spacing={3}>
            <TextField
              label="Extend Expiry (days)"
              type="number"
              value={editForm.expires_in_days}
              onChange={(e) => setEditForm((prev) => ({ ...prev, expires_in_days: e.target.value }))}
              fullWidth
              helperText="1–365 days from now"
              inputProps={{ min: 1, max: 365 }}
            />
            <TextField
              label="Recipient Email"
              type="email"
              value={editForm.recipient_email}
              onChange={(e) => setEditForm((prev) => ({ ...prev, recipient_email: e.target.value }))}
              fullWidth
              helperText="Updating email will send the claim link to the new address"
            />
            <TextField
              label="Recipient Phone"
              value={editForm.recipient_phone}
              onChange={(e) => setEditForm((prev) => ({ ...prev, recipient_phone: e.target.value }))}
              fullWidth
              helperText="Updating phone will send the claim SMS to the new number"
            />
          </Stack>
        </MuiDialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedTransfer(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={updateTransferMutation.isLoading}
            onClick={() => {
              if (selectedTransfer) {
                const payload = {};
                if (editForm.expires_in_days) payload.expires_in_days = parseInt(editForm.expires_in_days, 10);
                if (editForm.recipient_email && editForm.recipient_email !== selectedTransfer.recipient_email) {
                  payload.recipient_email = editForm.recipient_email;
                }
                if (editForm.recipient_phone && editForm.recipient_phone !== selectedTransfer.recipient_phone) {
                  payload.recipient_phone = editForm.recipient_phone;
                }
                if (Object.keys(payload).length > 0) {
                  updateTransferMutation.mutate({ id: selectedTransfer.id, data: payload });
                } else {
                  setSnackbarMessage("No changes to save");
                  setSnackbarSeverity('info');
                  setSnackbarOpen(true);
                  setEditDialogOpen(false);
                }
              }
            }}
          >
            {updateTransferMutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}